import { memo, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Award, Upload, Loader2, Lock, ChevronLeft, ChevronRight, CalendarRange, Gauge, Unlink, AlertCircle, Clock, Search, FileSpreadsheet, FileText, History, EyeOff, Eye, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import ImportDialog from './MotivareImportDialog';
import NonTimeSheet from './MotivareNonTimeSheet';
import LevelHistorySheet from './MotivareLevelHistorySheet';
import ImportsManager from './MotivareImportsManager';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const MONTHS = ['', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
const lei = (v) => `${(v ?? 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei`;
const qty = (v) => (Number.isInteger(v) ? v : (v ?? 0).toLocaleString('ro-RO', { maximumFractionDigits: 2 }));
const pct = (v) => (v == null ? '—' : `${Math.round(v * 1000) / 10}%`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const fmtP = (p) => (p ? `${p.start.slice(8, 10)}.${p.start.slice(5, 7)} – ${p.end.slice(8, 10)}.${p.end.slice(5, 7)}` : '—');

const matchAgent = (a, q) => {
  const s = q.trim().toLowerCase();
  return !s || (a.indicativ || '').toLowerCase().includes(s) || (a.name || '').toLowerCase().includes(s) || (a.employee_name || '').toLowerCase().includes(s);
};
const byAssoc = (a, mode) => mode === 'all' || (mode === 'assigned' ? !!a.employee_id : !a.employee_id);

const LEVEL_STYLE = { 0: 'bg-red-100 text-red-700', 1: 'bg-orange-100 text-orange-700', 2: 'bg-amber-100 text-amber-700', 3: 'bg-lime-100 text-lime-700', 4: 'bg-emerald-100 text-emerald-700', 5: 'bg-green-200 text-green-800' };
export const LevelBadge = ({ level, label, testId }) => (
  level == null
    ? <span className="text-xs text-slate-400" data-testid={testId}>—</span>
    : <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${LEVEL_STYLE[level] || 'bg-slate-100 text-slate-700'}`} data-testid={testId}>{level === 0 ? 'ZERO' : `L${level}`}</span>
);

const MotivareTab = memo(function MotivareTab({ canEdit = true }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [awbRow, setAwbRow] = useState(null);
  const [histRow, setHistRow] = useState(null);
  const [hideCalc, setHideCalc] = useState(() => localStorage.getItem('motivare_hide_calc') === '1');
  const toggleCalc = () => setHideCalc((h) => { localStorage.setItem('motivare_hide_calc', h ? '0' : '1'); return !h; });

  const download = async (format) => {
    setExporting(true);
    try {
      const r = await axios.get(`${API_URL}/motivare/export`, { ...auth(), params: { year, month, format }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Motivare_${year}-${String(month).padStart(2, '0')}.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Exportul a eșuat'); }
    finally { setExporting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/motivare/results`, { ...auth(), params: { year, month } });
      setData(r.data);
    } catch { toast.error('Nu s-au putut încărca datele Motivare'); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const shift = (d) => {
    let m = month + d, y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const onSaved = (res) => {
    setImportOpen(false);
    if (res.year !== year || res.month !== month) { setYear(res.year); setMonth(res.month); } else load();
  };

  const allRows = data?.rows || [];
  const rows = allRows.filter((r) => matchAgent(r, query) && byAssoc(r, mode));
  const cols = data?.columns || [];
  const unassigned = allRows.filter((r) => !r.employee_id).length;
  const noCnp = allRows.filter((r) => r.employee_id && !r.has_cnp);
  const showDed = !!data?.settings?.apply_deductions;
  const showKm = !!data?.km_active;
  const kmType = data?.settings?.km_level_type || 'hd';
  const hasOohLevel = !!data?.has_levels_ooh;
  const group = (t) => cols.filter((c) => c.level_type === t);
  const hdCols = group('hd'), oohCols = group('ooh'), noneCols = group('none');
  const showOoh = oohCols.length > 0 || (showKm && kmType === 'ooh');
  const TYPE_LABEL = { hd: 'HD', ooh: 'OOH', none: 'fără Level' };
  const H = ({ children, className = '' }) => <TableHead className={`text-right align-bottom max-w-[90px] leading-tight text-xs py-2 ${className}`}>{children}</TableHead>;
  const colCount = 3 + cols.length + (showKm ? 1 : 0) + (hideCalc ? 1 : 3) + (hasOohLevel && showOoh ? (hideCalc ? 1 : 3) : 0) + (showDed && !hideCalc ? 1 : 0) + (hideCalc ? 0 : 1);

  const ItemCells = ({ r, list }) => list.map((c) => {
    const it = r.items.find((i) => i.key === c.key) || { qty: 0, amount: 0 };
    return (
      <TableCell key={c.key} className="text-right whitespace-nowrap">
        <div className="text-sm">{qty(it.qty)}</div>
        {!hideCalc && <div className="text-xs text-slate-500">{lei(it.amount)}</div>}
      </TableCell>
    );
  });
  const KmCell = ({ r }) => (
    <TableCell className="text-right whitespace-nowrap" data-testid={`motivare-km-${r.indicativ}`}>
      <div className="text-sm">{r.employee_id ? `${qty(r.km)} km` : '—'}</div>
      {r.employee_id && !hideCalc && <div className="text-xs text-slate-500">{lei(r.km_amount)}</div>}
    </TableCell>
  );
  const LevelCells = ({ r, type }) => {
    const isHd = type === 'hd';
    const level = isHd ? r.level : r.level_ooh;
    const coef = isHd ? r.coefficient : r.coefficient_ooh;
    const base = r.parts?.[type] || 0;
    return (
      <>
        <TableCell className="text-center whitespace-nowrap">
          {isHd && r.nontime_count > 0 ? (
            <button type="button" onClick={() => setAwbRow(r)} className="inline-flex flex-col items-center hover:opacity-80" title={`${r.nontime_count} colete întârziate — click pentru listă`} data-testid={`motivare-awb-btn-${r.indicativ}`}>
              <LevelBadge level={level} testId={`motivare-level-${r.indicativ}`} />
              <span className="text-[10px] text-red-600 underline decoration-dotted">{r.nontime_count} AWB</span>
            </button>
          ) : <LevelBadge level={level} testId={`motivare-level-${isHd ? '' : 'ooh-'}${r.indicativ}`} />}
          {r.backup && level != null && <div className="text-[10px] text-violet-700 mt-0.5" data-testid={`motivare-backup-${r.indicativ}`}>back-up · raport {(isHd ? r.level_raw : r.level_ooh_raw) === 0 ? 'ZERO' : `L${isHd ? r.level_raw : r.level_ooh_raw}`}</div>}
          {isHd && r.otd != null && <div className="text-[11px] text-slate-400 mt-0.5">OTD {pct(r.otd)}</div>}
          {isHd && level != null && (
            <button type="button" onClick={() => setHistRow(r)} className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline mt-0.5" title="Istoric indicatori Level pe zile" data-testid={`motivare-hist-btn-${r.indicativ}`}>
              <History className="w-3 h-3" /> istoric
            </button>
          )}
        </TableCell>
        {!hideCalc && (
          <>
            <TableCell className="text-right text-sm whitespace-nowrap" data-testid={`motivare-coef-${isHd ? '' : 'ooh-'}${r.indicativ}`}>× {coef}%</TableCell>
            <TableCell className="text-right whitespace-nowrap bg-slate-50/60" data-testid={`motivare-bonus-${type}-${r.indicativ}`}>
              <div className="text-sm font-semibold">{lei(Math.round(base * coef) / 100)}</div>
              <div className="text-[11px] text-slate-400">din {lei(base)}</div>
            </TableCell>
          </>
        )}
      </>
    );
  };

  return (
    <div className="space-y-4" data-testid="motivare-tab-content">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Award className="w-5 h-5 text-slate-600" /> Motivare</h2>
          <p className="text-sm text-slate-500">Bonusare curieri pe baza raportului de activitate și a nivelului de calitate</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 border rounded-lg px-1 bg-white">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="motivare-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium min-w-[140px] text-center" data-testid="motivare-month-label">{MONTHS[month]} {year}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="motivare-next-month"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          {allRows.length > 0 && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => download('xlsx')} disabled={exporting} data-testid="motivare-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
              <Button variant="outline" size="sm" onClick={() => download('pdf')} disabled={exporting} data-testid="motivare-export-pdf"><FileText className="w-4 h-4 mr-1" /> PDF</Button>
            </div>
          )}
          {allRows.length > 0 && (
            <Button variant={hideCalc ? 'default' : 'outline'} size="sm" onClick={toggleCalc} className={hideCalc ? 'bg-slate-800 hover:bg-slate-700' : ''} data-testid="motivare-toggle-calc">
              {hideCalc ? <><Eye className="w-4 h-4 mr-1" /> Arată calculul</> : <><EyeOff className="w-4 h-4 mr-1" /> Ascunde calculul</>}
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setManageOpen(true)} data-testid="motivare-manage-imports-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Gestionează încărcările
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => setImportOpen(true)} data-testid="motivare-import-btn">
              <Upload className="w-4 h-4 mr-2" /> Actualizează raportarea
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-lg border bg-white px-4 py-3 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm" data-testid="motivare-status">
        {data?.activity_period ? (
          <>
            <span className="flex items-center gap-2"><CalendarRange className="w-4 h-4 text-blue-600" /> <span className="text-slate-500">Perioadă raportare:</span> <b data-testid="motivare-activity-period">{fmtP(data.activity_period)}</b></span>
            <span className="flex items-center gap-2"><Gauge className="w-4 h-4 text-emerald-600" /> <span className="text-slate-500">Level:</span> <b data-testid="motivare-level-period">{data.has_levels ? fmtP(data.level_period) : 'lipsă'}</b>
              {data.level_carried && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold" data-testid="motivare-level-carried" title={`Încă nu a venit raportul Level pe luna aceasta — se folosește ultimul nivel cunoscut (până la ${(data.level_carried.from_day || '').slice(8, 10)}.${(data.level_carried.from_day || '').slice(5, 7)})`}>reportat din luna precedentă</span>}</span>
            <span className="flex items-center gap-2 text-xs text-slate-500"><Clock className="w-3.5 h-3.5" /> actualizat {fmtDate(data.imported_at)}</span>
          </>
        ) : (
          <span className="text-slate-500">Nicio raportare pentru această lună.</span>
        )}
        {data?.locked && <span className="flex items-center gap-1 text-xs text-slate-600"><Lock className="w-3.5 h-3.5" /> Istoric — luna este blocată</span>}
        {data?.activity_period && !data.has_levels && <span className="flex items-center gap-1 text-xs text-amber-600"><AlertCircle className="w-3.5 h-3.5" /> Level neactualizat — se aplică coeficientul pentru nivel necunoscut</span>}
        {data?.has_levels && allRows.length > 0 && !allRows.some((r) => r.nontime_count > 0) && <span className="flex items-center gap-1 text-xs text-amber-600" data-testid="motivare-no-awb-warning"><AlertCircle className="w-3.5 h-3.5" /> Raportul Level nu conține AWB-uri (foaia Lista_NonTime) — reimportă perechea Activitate + Level pentru a le încărca</span>}
        {cols.length === 0 && data?.activity_period && <span className="flex items-center gap-1 text-xs text-amber-600"><AlertCircle className="w-3.5 h-3.5" /> Nicio coloană activă — configurează în Setări Motivare</span>}
        {unassigned > 0 && <span className="flex items-center gap-1 text-xs text-amber-600"><Unlink className="w-3.5 h-3.5" /> {unassigned} agenți neasociați (ignorați la calcul)</span>}
        {noCnp.length > 0 && (
          <span className="flex items-center gap-1 text-xs text-red-600" title={noCnp.map((r) => r.employee_name).join(', ')} data-testid="motivare-no-cnp">
            <AlertCircle className="w-3.5 h-3.5" /> {noCnp.length} angajați asociați fără CNP — nu ajung în Costuri personal: {noCnp.map((r) => r.employee_name).join(', ')}
          </span>
        )}
      </div>

      {allRows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="motivare-filters">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2" />
            <Input className="h-8 w-64 pl-8 text-sm bg-white" placeholder="Caută indicativ / nume / angajat..." value={query} onChange={(e) => setQuery(e.target.value)} data-testid="motivare-search" />
          </div>
          <div className="flex rounded-md border overflow-hidden text-xs bg-white" data-testid="motivare-mode">
            {[['all', 'Toți'], ['assigned', 'Asociați'], ['unassigned', 'Neasociați']].map(([m, l]) => (
              <button key={m} type="button" onClick={() => setMode(m)} className={`px-3 py-1.5 ${mode === m ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`motivare-mode-${m}`}>{l}</button>
            ))}
          </div>
          <span className="text-xs text-slate-500" data-testid="motivare-visible-count">{rows.length} din {allRows.length} agenți</span>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {hideCalc && allRows.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 text-xs text-slate-700 bg-slate-100 border-b" data-testid="motivare-calc-hidden-banner">
              <EyeOff className="w-3.5 h-3.5" /> Mod discuție — calculul (coeficienți, sume, total bonus) este ascuns. Apasă „Arată calculul” pentru a-l afișa.
            </div>
          )}
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 p-8" data-testid="motivare-loading"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="motivare-empty">
              {allRows.length > 0 ? 'Niciun agent nu corespunde filtrului.' : data?.locked ? 'Nu există date importate pentru această lună.' : 'Apasă „Actualizează raportarea” și încarcă raportul de activitate împreună cu raportul Level.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="motivare-results-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white z-10 align-bottom">Agent</TableHead>
                    <TableHead className="align-bottom">Angajat</TableHead>
                    <TableHead className="text-right align-bottom">Zile</TableHead>
                    {hdCols.map((c) => <H key={c.key}>{c.label}</H>)}
                    {showKm && kmType === 'hd' && <H>Km</H>}
                    <H className="text-center max-w-[70px]">Level HD</H>
                    {!hideCalc && <><H className="max-w-[60px]">Coef. HD</H><H className="max-w-[80px] bg-slate-50/60">Bonus HD</H></>}
                    {showKm && kmType !== 'hd' && <H>Km</H>}
                    {oohCols.map((c) => <H key={c.key}>{c.label}</H>)}
                    {hasOohLevel && showOoh && <><H className="text-center max-w-[70px]">Level OOH</H>{!hideCalc && <><H className="max-w-[60px]">Coef. OOH</H><H className="max-w-[80px] bg-slate-50/60">Bonus OOH</H></>}</>}
                    {noneCols.map((c) => <H key={c.key}>{c.label}</H>)}
                    {showDed && !hideCalc && <H>Rețineri</H>}
                    {!hideCalc && <H className="max-w-[80px]">Total bonus</H>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.agent_id} className={r.employee_id ? '' : 'text-slate-400'} data-testid={`motivare-row-${r.indicativ}`}>
                      <TableCell className="sticky left-0 bg-white z-10">
                        <div className="font-mono text-xs">{r.indicativ}</div>
                        <div className="text-sm">{r.name}</div>
                      </TableCell>
                      <TableCell className="text-sm">{r.employee_name || <span className="italic flex items-center gap-1"><Unlink className="w-3 h-3" /> neasociat</span>}</TableCell>
                      <TableCell className="text-right text-sm">{qty(r.days)}</TableCell>
                      <ItemCells r={r} list={hdCols} />
                      {showKm && kmType === 'hd' && <KmCell r={r} />}
                      <LevelCells r={r} type="hd" />
                      {showKm && kmType !== 'hd' && <KmCell r={r} />}
                      <ItemCells r={r} list={oohCols} />
                      {hasOohLevel && showOoh && <LevelCells r={r} type="ooh" />}
                      <ItemCells r={r} list={noneCols} />
                      {showDed && !hideCalc && <TableCell className="text-right text-sm text-red-600">{r.deductions ? `-${lei(r.deductions)}` : '—'}</TableCell>}
                      {!hideCalc && (
                        <TableCell className="text-right font-semibold whitespace-nowrap" data-testid={`motivare-total-${r.indicativ}`}>
                          {r.employee_id ? (r.eligible ? lei(r.total) : <span className="text-xs font-normal text-slate-500">sub minim zile</span>) : '—'}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {!hideCalc && (
                    <TableRow className="bg-slate-50 font-semibold">
                      <TableCell className="sticky left-0 bg-slate-50 z-10" colSpan={colCount - 1}>Total</TableCell>
                      <TableCell className="text-right whitespace-nowrap" data-testid="motivare-grand-total">{lei(rows.reduce((s, r) => s + (r.total || 0), 0))}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onSaved={onSaved} />
      <ImportsManager open={manageOpen} onClose={() => setManageOpen(false)} year={year} month={month} onChanged={load} />
      <NonTimeSheet row={awbRow} year={year} month={month} onClose={() => setAwbRow(null)} />
      <LevelHistorySheet row={histRow} year={year} month={month} onClose={() => setHistRow(null)} />
    </div>
  );
});

export default MotivareTab;
